import React, { useState, useLayoutEffect, useEffect, useCallback, useRef } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import {
  Col,
  Row,
  Badge,
  Spinner,
  Form,
  Modal,
  Button,
  Tabs,
  Tab,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { Warning, PlusCircle, Plus, Flag, Pencil, CaretCircleDown, Info } from 'phosphor-react'
import api from '../../../services/api'
import { toast } from 'react-toastify'
import { errorApi } from '../../../utils/errorApi'

import styles from './styles.module.css'
import ConfirmationModal from '../../../components/ConfirmationModal'
import { DateTime } from 'luxon'
import { formatDateTSX } from '../../../utils/tsxFormat'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../components/SearchInput'
import StoreRequestModal from '../../../components/StoreRequestModal'
import EditRequestModal from '../../../components/EditRequestModal'
import UpdateRequestWeightModal from '../../../components/UpdateRequestWeightModal'

interface IRequest {
  invoice: string
  request_number: string
  invoice_number: string
  receiver_name: string
  status: string
  cep: string
  city: string
  state: string
  neighborhood: string
  street: string
  street_number: string
  complement: string
  start_date: Date
  weight: number
  has_priority: boolean
  id: number
  created_at: Date
  updated_at: Date
  vehicle_id: number
}

interface IVehicles {
  board: string
  model: string
  id: string
  capacity: string
  allocated_weight: number
  color: string
}

const limit = 5

const Pedidos: React.FC = () => {
  const [vehicles, setVehicles] = useState<IVehicles[]>([])
  const [selectedVehicileId, setSelectedVehicleId] = useState(0)

  const [vehiclesUnavailables, setVehiclesUnavailables] = useState<IVehicles[]>()
  const [requests, setRequests] = useState<IRequest[]>()
  const [requestsWithoutStartDate, setRequestsWithoutStartDate] = useState<IRequest[]>()

  const [hasMoreRequests, setHasMoreRequests] = useState(true)

  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const [requestModal, setRequestModal] = useState<IRequest>()

  const [editRequestIndex, setEditRequestIndex] = useState<number>(-1)
  const [editRequestDate, setEditRequestDate] = useState<Date | null>(null)

  const [lastRequests, setLastRequests] = useState<
    {
      id: number
      invoice: string
      request_number: string
      invoice_number: string
      board: string
      color: string
      model: string
      weight: number
      vehicle_id: number
    }[]
  >([])
  const [showDelivery, setShowDelivery] = useState(false)

  const [showStoreRequestModal, setShowStoreRequestModal] = useState(false)
  const [showEditRequestModal, setShowEditRequestModal] = useState(false)

  const [selectedTab, setSelectedTab] = useState<string>('pedidos')

  const [barcodeMode, setBarcodeMode] = useState(() => {
    const saved = localStorage.getItem('barcodeMode')
    return saved ? JSON.parse(saved) : false
  })
  const barcodeInputRef = useRef<HTMLInputElement>(null)

  const [showRedoRequestModal, setShowRedoRequestModal] = useState(false)
  const [showUpdateRequestWeightModal, setShowUpdateRequestWeightModal] = useState(false)

  const [redoRequest, setRedoRequest] = useState<IRequest>()
  const [redoRequestModalMessage, setRedoRequestModalMessage] = useState('')

  const { isLoading, setIsLoading } = useLoading()

  const status = {
    unallocated: { name: 'Não alocado', color: '#cca300' },
    allocated: { name: 'Alocado', color: '#00BFFF' },
    in_progress: { name: 'Em andamento', color: '#120a8f' },
    on_route: { name: 'Em rota', color: '#053C5E' },
    delivered: { name: 'Entregue', color: '#089c08' },
    canceled: { name: 'Cancelado', color: '#ff0000' },
  }

  const handleAddRequestDate = async (requestId: number) => {
    setIsLoading(true)

    try {
      const { data } = await api.put(`/delivery-manager/requests/${requestId}`, {
        start_date: editRequestDate,
      })
      setEditRequestIndex(-1)
      setEditRequestDate(null)

      handleRequestUpdate(data)
      toast.success(`Data de entrega adicionada ao pedido com sucesso!`)
    } catch (err) {
      toast.error(errorApi(err, 'Erro ao adicionar data de entrega'))
    }

    setIsLoading(false)
  }

  useEffect(() => {
    localStorage.setItem('barcodeMode', JSON.stringify(barcodeMode))
    if (barcodeMode) {
      setSelectedTab('veiculos')
    }
  }, [barcodeMode])

  // useEffect que vai garantir que o barcode input permaneça sempre focado
  useEffect(() => {
    if (!barcodeMode || !selectedVehicileId) return

    const handleClickOutside = (event: MouseEvent) => {
      // Verifica se o clique foi fora do barcode input
      const isClickInsideInput = barcodeInputRef.current?.contains(event.target as Node)
      const htmlElement = event.target as HTMLElement

      const isClickInsideInteractiveElement =
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(htmlElement.tagName) ||
        htmlElement.isContentEditable

      if (!isClickInsideInput && !isClickInsideInteractiveElement) {
        barcodeInputRef.current?.focus() // Foca novamente no input
      }
    }
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [barcodeMode, selectedVehicileId])

  //useEffect para garantir que, ao fechar modais, barcode input sempre seja focado
  useEffect(() => {
    const notAllModalsAreClosed =
      showDelivery ||
      showEditRequestModal ||
      showRedoRequestModal ||
      showStoreRequestModal ||
      showUpdateRequestWeightModal

    if (notAllModalsAreClosed) return
    focusBarcodeInput()
  }, [
    showDelivery,
    showEditRequestModal,
    showRedoRequestModal,
    showStoreRequestModal,
    showUpdateRequestWeightModal,
  ])

  useLayoutEffect(() => {
    setIsLoading(true)
    async function loadData() {
      try {
        const today = DateTime.now().toISODate()
        const { data } = await api.get(
          `/delivery-manager/vehicles/deliveries/available?date=${today}`,
        )

        setVehicles(data.vehiclesAvailables)
        setVehiclesUnavailables(data.vehiclesUnavailables)
      } catch (err) {
        toast.error('Falha ao buscar os dados! Atualize a página.')
      }
      setIsLoading(false)
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateVehicles = () => {
    setIsLoading(true)
    async function loadData() {
      try {
        const today = DateTime.now().toISODate()
        const { data } = await api.get(
          `/delivery-manager/vehicles/deliveries/available?date=${today}`,
        )

        if (!data.vehiclesAvailables.find((v: any) => v.id === selectedVehicileId)) {
          handleChangeSelectedVehicleId(0)
        }

        setVehicles(data.vehiclesAvailables)
        setVehiclesUnavailables(data.vehiclesUnavailables)
      } catch (err) {
        toast.error('Falha ao buscar os dados! Atualize a página.')
      }
      setIsLoading(false)
    }
    loadData()
  }

  const handleUnallocate = async (id: number) => {
    setIsLoading(true)
    try {
      const lastRequest = lastRequests.filter((r) => r.id === id)[0]
      await api.put(`/delivery-manager/requests/${lastRequest?.id}?unbind=true`, {
        ...lastRequest,
      })
      setLastRequests(lastRequests.filter((r) => r.invoice !== lastRequest.invoice))

      setRequests(
        requests?.map((r) => {
          if (Number(r.id) === lastRequest.id) {
            r = {
              ...r,
              status: 'unallocated',
              vehicle_id: 0,
            }
          }
          return r
        }),
      )

      const weight =
        vehicles.find((v) => Number(v.id) === lastRequest!.vehicle_id)!.allocated_weight -
        lastRequest.weight

      updateVehicles()
      toast.success(`Entrega desalocada do veículo com sucesso! Peso atual da carga: ${weight} kg`)
    } catch (err) {
      toast.error(errorApi(err, 'Erro ao desalocar a entrega! Tente novamente!'))
    }
    setIsLoading(false)
  }

  const loadRequests = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch

    if (!hasMoreRequests && !isSearchDifferent) return

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreRequests(true)
    }

    let apiUrl = `/delivery-manager/requests?status=unallocated&limit=${limit}&search=${search}`

    const offset = requests && !isSearchDifferent ? requests.length : 0

    if (offset > 0) apiUrl += `&offset=${offset}`

    try {
      setIsLoading(true)

      const { data } = await api.get(apiUrl)

      if (!data.length) {
        if (isSearchDifferent) setRequests([])
        setHasMoreRequests(false)

        if (offset === 0) toast.info('Não há mais pedidos registrados.')
      } else {
        if (!requests) setRequests(data)
        else setRequests(isSearchDifferent ? data : requests.concat(data))

        if (data.length < limit) setHasMoreRequests(false)
      }
    } catch (err) {
      toast.error(errorApi(err, 'Erro ao buscar pedidos'))
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMoreRequests, requests?.length, search])

  useEffect(() => {
    if (!requests || requests.length === 0) loadRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    setIsLoading(true)
    async function loadData() {
      try {
        const { data } = await api.get(
          `/delivery-manager/requests?start_date_begin=${null}&status=unallocated`,
        )
        setRequestsWithoutStartDate(data)
      } catch (err) {
        toast.error('Falha ao buscar os dados! Atualize a página.')
      }
      setIsLoading(false)
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeSelectedVehicleId = (value: number) => {
    setSelectedVehicleId(Number(value))
    focusBarcodeInput()
  }

  const clearBarcodeInput = () => {
    if (barcodeMode) {
      const barcodeInput = document.getElementById('barcode-input') as HTMLInputElement
      if (barcodeInput) {
        barcodeInput.value = ''
      }
    }
  }

  const allocate_vehicle = async (vehicle_id: string, request: IRequest) => {
    try {
      setIsLoading(true)
      if (vehicle_id !== '0') {
        const { data } = await api.post(`/delivery-manager/vehicles/requests/${request.id}/allocate`, {
          vehicle_id: vehicle_id,
          allocated_weight: request.weight,
          status: 'in_loading',
          request_id: request.id,
        })

        setRequests(
          requests?.map((r) => {
            if (Number(r.id) === request.id) {
              r = {
                ...r,
                status: 'allocated', //status da request agora é "allocated"
                vehicle_id: Number(vehicle_id),
              }
            }
            return r
          }),
        )

        // Verifica se a requisição estava em requestsWithoutStartDate e, caso sim, remove
        if (requestsWithoutStartDate && requestsWithoutStartDate.length) {
          setRequestsWithoutStartDate((prevWithoutStartDate = []) =>
            prevWithoutStartDate.filter((req) => req.id !== request.id),
          )
        }

        toast.success(
          `Entrega alocada ao veículo com sucesso! ${
            data.data ? 'Peso atual da carga: ' + data.data.allocated_weight + ' kg' : ''
          }`,
        )
      } else {
        const weight =
          vehicles.find((v) => Number(v.id) === request!.vehicle_id)!.allocated_weight -
          request.weight

        await api.put(`/delivery-manager/requests/${request.id}?unbind=true`, {
          ...request,
        })

        setRequests(
          requests?.map((r) => {
            if (Number(r.id) === request.id) {
              r = {
                ...r,
                status: 'unallocated',
                vehicle_id: 0,
              }
            }
            return r
          }),
        )

        setVehicles(
          vehicles.map((v) => {
            if (Number(v.id) === request.vehicle_id) {
              v.allocated_weight = weight
            }
            return v
          }),
        )
        setLastRequests(lastRequests.filter((r) => r.id !== request.id))

        toast.success(
          `Entrega desalocada do veículo com sucesso! Peso atual da carga: ${weight.toFixed(2)} kg`,
        )
      }
      updateVehicles()
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao alocar o veículo!'))
    }
    setIsLoading(false)
  }

  const getVehicle = (id: number) => {
    return vehicles.filter((v) => Number(v.id) === id)[0]
  }

  const handleRedoRequest = async (request: IRequest | null) => {
    try {
      setIsLoading(true)

      let requestToRedo = request ?? redoRequest
      requestToRedo!.start_date = new Date()
      let requestUpdated: IRequest
      // Se o status for unallocated, apenas atualiza a data da request
      if (requestToRedo!.status === 'unallocated') {
        requestToRedo!.start_date = new Date(new Date(Date.now()).setHours(0, 0, 0, 0))
        const { data } = await api.put(
          `/delivery-manager/requests/${requestToRedo!.id}`,
          requestToRedo,
        )
        requestUpdated = data
      } else {
        // Se não, cria uma request nova com o mesmo invoice
        const { data } = await api.post(
          `/delivery-manager/requests?redo_request=true`,
          requestToRedo,
        )
        requestUpdated = data
      }
      setLastRequests([
        ...lastRequests,
        {
          id: requestUpdated.id,
          invoice: requestUpdated.invoice,
          invoice_number: requestUpdated.invoice_number,
          request_number: requestUpdated.request_number,
          board: getVehicle(selectedVehicileId).board,
          color: getVehicle(selectedVehicileId).color,
          model: getVehicle(selectedVehicileId).model,
          weight: requestUpdated.weight,
          vehicle_id: selectedVehicileId,
        },
      ])
      await allocate_vehicle(String(selectedVehicileId), requestUpdated)
    } catch (err) {
      toast.error(errorApi(err, 'Erro ao criar requisição de entrega.'))
    }
    setIsLoading(false)
    setShowRedoRequestModal(false)
  }

  const handleBarcode = async (invoice: string) => {
    if (invoice.length !== 44) return //44 == nfe length
    if (!requests) return

    if (selectedVehicileId === 0) {
      toast.error('Por favor, selecione o veículo para qual a entrega deve ser adicionada.')
      return
    }

    const request = requests.filter((r) => r.invoice === invoice)
    let request_allocate: IRequest = request[0]
    if (!request.length) {
      // se não existia no front
      try {
        setIsLoading(true)
        const { data } = await api.get(`delivery-manager/requests?search=${invoice}`)
        setIsLoading(false)

        if (!data.length) {
          toast.error('Nenhuma requisição de entrega cadastrada com a nota fiscal informada.')
          clearBarcodeInput()
          return
        }

        const selectedDate = new Date().toISOString().split('T')[0]

        const request_unallocated: IRequest[] = data.filter(
          (r: IRequest) => r.status === 'unallocated',
        )
        if (request_unallocated.length) {
          let requestDate = new Date(request_unallocated[0].start_date).toISOString().split('T')[0]
          if (requestDate !== selectedDate) {
            setRedoRequest(request_unallocated[0])
            await handleRedoRequest(request_unallocated[0])
            clearBarcodeInput()
            return
          }
          request_allocate = request_unallocated[0]
        }

        const request_onRoute: IRequest[] = data.filter((r: IRequest) => r.status === 'on_route')
        if (request_onRoute.length) {
          toast.error('Já existe uma requisição de entrega em rota com a nota fiscal informada.')
          clearBarcodeInput()
          return
        }

        const request_DeliveredOrCanceled: IRequest[] = data.filter((r: IRequest) =>
          ['delivered', 'canceled'].includes(r.status),
        )
        if (request_DeliveredOrCanceled.length) {
          setRedoRequest(request_DeliveredOrCanceled[0])
          setRedoRequestModalMessage(
            'A nota fiscal informada já consta como cancelada ou entregue no sistema. Ela pertence a uma reentrega ocasionada por um problema na entrega (ocorrência)?',
          )
          clearBarcodeInput()
          setShowRedoRequestModal(true)
          return
        }
      } catch (err) {
        toast.error(errorApi(err, 'Erro ao buscar requisição de entrega.'))
      }
      setIsLoading(false)
    }

    if (request_allocate) {
      setLastRequests([
        ...lastRequests,
        {
          id: request_allocate.id,
          invoice: request_allocate.invoice,
          request_number: request_allocate.request_number,
          invoice_number: request_allocate.invoice_number,
          board: getVehicle(selectedVehicileId).board,
          color: getVehicle(selectedVehicileId).color,
          model: getVehicle(selectedVehicileId).model,
          weight: request_allocate.weight,
          vehicle_id: selectedVehicileId,
        },
      ])
      await allocate_vehicle(String(selectedVehicileId), request_allocate)
    }
    clearBarcodeInput()
  }

  const handleRequestUpdate = async (updatedRequest: IRequest) => {
    setRequestModal(updatedRequest)

    let updatedRequests = requests ?? []
    let updatedRequestsWithoutStartDate = requestsWithoutStartDate ?? []

    const requestIndex = updatedRequests.findIndex((r) => r.id === updatedRequest.id)
    const requestWithoutStartDateIndex = updatedRequestsWithoutStartDate.findIndex(
      (r) => r.id === updatedRequest.id,
    )

    // Caso a updatedRequest esteja em updatedRequests
    if (requestIndex !== -1) {
      //Se a request ainda possui start_date...
      if (updatedRequest.start_date) {
        updatedRequests[requestIndex] = updatedRequest
      } else {
        updatedRequests.splice(requestIndex, 1) // Remove de updatedRequests
        updatedRequestsWithoutStartDate = [updatedRequest, ...updatedRequestsWithoutStartDate] // Adiciona em updatedRequestsWithoutStartDate
      }
    }

    // Caso a updatedRequest esteja em updatedRequestsWithoutStartDate
    if (requestWithoutStartDateIndex !== -1) {
      // Se algum start_date foi adicionado...
      if (updatedRequest.start_date) {
        updatedRequests = [updatedRequest, ...updatedRequests] // Adiciona a updatedRequests
        updatedRequestsWithoutStartDate.splice(requestWithoutStartDateIndex, 1) // Remove de updatedRequestsWithoutStartDate
      } else {
        updatedRequestsWithoutStartDate[requestWithoutStartDateIndex] = updatedRequest
      }
    }

    setRequests(updatedRequests)
    setRequestsWithoutStartDate(updatedRequestsWithoutStartDate)
  }

  const handleRequestStored = async (storedRequest: IRequest) => {
    if (storedRequest.start_date) {
      setRequests((prev) => [storedRequest, ...(prev || [])])
    } else {
      setRequestsWithoutStartDate((prev) => [storedRequest, ...(prev || [])])
    }
  }

  const handleRequestRemoved = async (removedRequestId: number) => {
    setShowDelivery(false)

    if (requests && requests.length) {
      const updatedRequests = requests.filter((request) => request.id !== removedRequestId)
      if (updatedRequests.length !== requests.length) setRequests(updatedRequests)
    }
    if (requestsWithoutStartDate && requestsWithoutStartDate.length) {
      const updatedRequests = requestsWithoutStartDate.filter(
        (request) => request.id !== removedRequestId,
      )
      if (updatedRequests.length !== requestsWithoutStartDate.length)
        setRequestsWithoutStartDate(updatedRequests)
    }
  }

  const focusBarcodeInput = () => {
    setTimeout(() => {
      barcodeInputRef.current?.focus() // Foca novamente no input
    }, 300)
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Pedidos">
      <LoadingSpinner text="" />
      <Row className={styles.topRow} style={{ marginInline: 30, marginTop: 8 }}>
        <Col md={10}>
          {!barcodeMode && (
            <SearchInput
              md={10}
              placeholder="Digite a nota fiscal, o nome do cliente, o número do pedido ou o número da nota fiscal"
              showOptions={false}
              data={requests as never[]}
              setValue={setSearch}
              searchValue={search}
              handleSearch={loadRequests}
            />
          )}

          <Form className="my-3">
            <Form.Check // prettier-ignore
              type="switch"
              id="custom-switch"
              label="Modo leitura de código de barras"
              checked={barcodeMode}
              style={{ fontSize: 14, marginTop: barcodeMode ? 0 : 10 }}
              className={barcodeMode ? styles.barcodeSwitch : ''}
              onChange={() => {
                setBarcodeMode(!barcodeMode)
                setSelectedTab(barcodeMode ? 'pedidos' : 'veiculos')
                setSelectedVehicleId(0)
              }}
            />
          </Form>
        </Col>
        <Col md={2} className="text-md-start text-center mt-md-0 mt-3">
          <Button className={styles.newRequestBtn} onClick={() => setShowStoreRequestModal(true)}>
            <Plus
              style={{ marginBottom: 4, marginRight: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Novo pedido
          </Button>
        </Col>
      </Row>

      {barcodeMode && (
        <Row className="mb-5 mx-4">
          <Row>
            <Col md={6}>
              <Form.Select
                className={styles.barcodeInput}
                onChange={(e) => handleChangeSelectedVehicleId(Number(e.target.value))}
              >
                <option value={0}>Selecione o veículo</option>
                {vehicles?.map((vehicle, index) => (
                  <option key={`selectvehicle_${index}`} value={vehicle.id}>
                    {`${vehicle.board} ( ${vehicle.model} ${vehicle.color} )`}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {selectedVehicileId > 0 && (
              <Col>
                <Form.Control
                  ref={barcodeInputRef}
                  id="barcode-input"
                  className={styles.barcodeInput}
                  type="text"
                  placeholder="Escaneie o código de barras ou digite a nota fiscal manualmente"
                  onChange={(e) => handleBarcode(e.target.value)}
                />
              </Col>
            )}
          </Row>
          {lastRequests.length !== 0 && (
            <>
              {lastRequests.map((request, index) => (
                <Col
                  key={`lastrequestcard_${index}`}
                  className={styles.lastRequestCard}
                  lg={4}
                  md={4}
                  xl={3}
                  xxl={2}
                >
                  <span className={styles.modalInvoice}>
                    {String(request.invoice_number ?? request.invoice)}
                  </span>
                  <span className={styles.cardSubtitle}>Alocado a:</span>
                  <span className={styles.vehicleInfo}>{request.board}</span>
                  <span className={styles.receiverName}>{`${request.model} ${request.color}`}</span>
                  <Button
                    className={styles.unallocateBtn}
                    onClick={async (id) => await handleUnallocate(request.id)}
                  >
                    Desfazer
                  </Button>
                </Col>
              ))}
            </>
          )}
        </Row>
      )}

      <Tabs
        className="mt-3 mt-md-0"
        fill={true}
        justify={true}
        activeKey={selectedTab}
        onSelect={(k: string | null) => k && setSelectedTab(k)}
      >
        <Tab
          eventKey="pedidos"
          title={
            <span
              className={styles.tabTitle}
              style={{
                fontWeight: selectedTab === 'pedidos' ? 600 : 400,
              }}
            >
              Pedidos
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip style={{ fontSize: 14 }}>Pedidos ainda pendentes de alocação</Tooltip>
                }
              >
                <div style={{ marginLeft: 5 }}>
                  <Info
                    size={18}
                    weight={selectedTab === 'pedidos' ? 'bold' : 'regular'}
                    color="#000000"
                  />
                </div>
              </OverlayTrigger>
            </span>
          }
        >
          <Row
            className={`${styles.requestCardsRow} justify-content-center justify-content-md-start`}
          >
            {isLoading && (
              <div
                style={{
                  textAlign: 'center',
                  marginTop: 40,
                }}
              >
                <Spinner
                  animation="border"
                  style={{
                    color: '#053C5E',
                    marginRight: 10,
                  }}
                />
              </div>
            )}
            {!requests?.length && !isLoading && (
              <Badge
                bg=""
                text="white"
                style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
              >
                <Warning
                  style={{ marginBottom: 2 }}
                  color="#FFFFFF"
                  size={'1.3rem'}
                  weight="bold"
                />{' '}
                Nenhum pedido desalocado encontrado
              </Badge>
            )}
            <>
              {requests?.map((request, index) => (
                <Col
                  key={`requestcard_${index}`}
                  className={styles.requestCard}
                  lg={4}
                  md={4}
                  xl={3}
                  xxl={2}
                >
                  <div className={styles.requestCardContainer}>
                    {Boolean(request.has_priority) && (
                      <Row className={styles.priorityRow}>
                        <>
                          <div className={styles.priorityText}>Possui prioridade</div>
                          <Flag
                            size={12}
                            weight="fill"
                            color="#DB222A"
                            style={{ padding: 0, width: 'fit-content' }}
                          ></Flag>
                        </>
                      </Row>
                    )}
                    <Form.Select
                      className={styles.allocateVehicleSelect}
                      onChange={async (e) => await allocate_vehicle(e.target.value, request)}
                      value={request.vehicle_id || '0'}
                    >
                      <option value="0">Selecione o veículo</option>
                      {vehicles?.map((vehicle, index) => (
                        <option key={`vehicleoption_${index}`} value={vehicle.id}>
                          {`${vehicle.board} (${vehicle.model} ${vehicle.color})`}
                        </option>
                      ))}
                    </Form.Select>
                    <span className={styles.cardInvoice}>
                      {String(request.invoice_number ?? request.invoice)}
                    </span>
                    {request.request_number && (
                      <span className={styles.cardRequestNumber}>
                        {String('Pedido ' + request.request_number)}
                      </span>
                    )}
                    <span className={styles.startDate}>
                      Feito em: {formatDateTSX(request.created_at, 'dd/MM/yyyy')}
                    </span>
                    <span className={styles.receiverName}>{request.receiver_name}</span>
                    <span className={styles.destination}>
                      {request.street}, {request.street_number}, {request.neighborhood}
                      {request.complement && <span>, {request.complement}</span>} - {request.city} -{' '}
                      {request.state}
                    </span>
                  </div>
                  <PlusCircle
                    className={styles.plusCircle}
                    fill="#053C5E"
                    size={28}
                    color="white"
                    onClick={() => [setShowDelivery(true), setRequestModal(request)]}
                  />
                </Col>
              ))}
              {hasMoreRequests && (
                <>
                  <p className={styles.loadMoreRequestsLabel}>
                    Existem mais requisições de entrega pendentes
                  </p>
                  <p className={styles.loadMoreRequests} onClick={() => loadRequests()}>
                    Carregar mais pedidos <CaretCircleDown weight="fill" color="#053C5E" />
                  </p>
                </>
              )}
            </>
          </Row>
        </Tab>
        <Tab
          eventKey="pedidos-sem-data"
          title={
            <span
              className={styles.tabTitle}
              style={{
                fontWeight: selectedTab === 'pedidos-sem-data' ? 600 : 400,
              }}
            >
              Pedidos sem data de entrega
              {requestsWithoutStartDate && (
                <div className={styles.notificationCircle}>
                  <p style={{ margin: 0 }}>
                    {requestsWithoutStartDate?.length > 99
                      ? '99+'
                      : requestsWithoutStartDate?.length}
                  </p>
                </div>
              )}
            </span>
          }
        >
          <Row
            className={`${styles.editDateCardsRow} justify-content-center justify-content-md-start`}
          >
            {!requestsWithoutStartDate?.length && !isLoading && (
              <Badge
                bg=""
                text="white"
                style={{
                  textAlign: 'center',
                  marginTop: 25,
                  marginBottom: 20,
                  backgroundColor: '#053c5e',
                }}
              >
                <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" />{' '}
                Nenhum pedido sem data de entrega
              </Badge>
            )}
            {requestsWithoutStartDate?.map((request, index) => (
              <Col
                key={`editdatecard_${index}`}
                className={styles.editDateCard}
                lg={4}
                md={4}
                xl={3}
                xxl={2}
              >
                {Boolean(request.has_priority) && (
                  <Row className={styles.priorityRow}>
                    <>
                      <div className={styles.priorityText}>Possui prioridade</div>
                      <Flag
                        size={12}
                        weight="fill"
                        color="#DB222A"
                        style={{ padding: 0, width: 'fit-content' }}
                      ></Flag>
                    </>
                  </Row>
                )}
                <div>
                  <span className={styles.cardInvoice}>
                    {String(request.invoice_number ?? request.invoice)}
                  </span>
                  {request.request_number && (
                    <span className={styles.cardRequestNumber}>
                      {String('Pedido ' + request.request_number)}
                    </span>
                  )}
                  <span className={styles.receiverName}>{request.receiver_name}</span>
                  <span className={styles.destination}>
                    {request.street}, {request.street_number}, {request.neighborhood}
                    {request.complement && <span>, {request.complement}</span>} - {request.city} -{' '}
                    {request.state}
                  </span>
                </div>
                {editRequestIndex === index ? (
                  <Row className="d-flex justify-content-center mx-2 mt-2">
                    <Form.Control
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setEditRequestDate(new Date(e.target.value))}
                    />
                    <Button
                      className={styles.saveDateBtn}
                      onClick={() => {
                        handleAddRequestDate(request.id)
                      }}
                    >
                      Salvar data
                    </Button>
                  </Row>
                ) : (
                  <Button className={styles.editDateBtn} onClick={() => setEditRequestIndex(index)}>
                    <Pencil fill="#053C5E" size={28} color="white" />
                    Adicionar data de entrega
                  </Button>
                )}
              </Col>
            ))}
          </Row>
        </Tab>
        <Tab
          eventKey="veiculos"
          title={
            <span
              className={styles.tabTitle}
              style={{
                fontWeight: selectedTab === 'veiculos' ? 600 : 400,
              }}
            >
              Veículos disponíveis
              {vehicles && (
                <div className={styles.counterBadge}>
                  <p style={{ margin: 0 }}>{vehicles?.length > 99 ? '99+' : vehicles?.length}</p>
                </div>
              )}
            </span>
          }
        >
          <Row
            className={`${styles.availableVehiclesRow} justify-content-center justify-content-md-start`}
          >
            {isLoading && (
              <div
                style={{
                  textAlign: 'center',
                  marginTop: 40,
                }}
              >
                <Spinner
                  animation="border"
                  style={{
                    color: '#053C5E',
                    marginRight: 10,
                  }}
                />
              </div>
            )}
            {!vehicles?.length && !isLoading && (
              <Badge
                bg=""
                text="white"
                style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
              >
                <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" />{' '}
                Nenhum veículo disponível!
              </Badge>
            )}
            {vehicles?.map((vehicle, index) => (
              <Col
                key={`availablevehicle_${index}`}
                className={styles.availableVehicleCard}
                title="Editar"
                lg={4}
                xl={3}
                md={4}
                xxl={2}
              >
                <span className={styles.board}>{vehicle.board}</span>
                <span className={styles.modelcolor}>{`${vehicle.model} ${vehicle.color}`}</span>
                <span className={styles.capacity}>
                  Capacidade:{' '}
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: 15,
                    }}
                  >
                    {vehicle.capacity} kg
                  </span>
                </span>
                <span className={styles.capacity}>
                  Peso alocado:{' '}
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: 15,
                    }}
                  >
                    {vehicle.allocated_weight ? vehicle.allocated_weight : '0'} kg
                  </span>
                </span>
              </Col>
            ))}
          </Row>
        </Tab>
        <Tab
          eventKey="veiculos-carga-completa"
          title={
            <span
              className={styles.tabTitle}
              style={{
                fontWeight: selectedTab === 'veiculos-carga-completa' ? 600 : 400,
              }}
            >
              Veículos com carga completa
              {vehiclesUnavailables && (
                <div className={styles.counterBadge}>
                  <p style={{ margin: 0 }}>
                    {vehiclesUnavailables?.length > 99 ? '99+' : vehiclesUnavailables?.length}
                  </p>
                </div>
              )}
            </span>
          }
        >
          <Row
            className={`${styles.unavailableVehiclesRow} justify-content-center justify-content-md-start`}
          >
            {isLoading && (
              <div
                style={{
                  textAlign: 'center',
                  marginTop: 40,
                }}
              >
                <Spinner
                  animation="border"
                  style={{
                    color: '#053C5E',
                    marginRight: 10,
                  }}
                />
              </div>
            )}
            {!vehiclesUnavailables?.length && !isLoading && (
              <Badge
                bg=""
                text="white"
                style={{
                  textAlign: 'center',
                  marginTop: 25,
                  marginBottom: 20,
                  backgroundColor: '#053c5e',
                }}
              >
                <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" />{' '}
                Nenhum veículo com carga completa!
              </Badge>
            )}
            {vehiclesUnavailables?.map((vehicle, index) => (
              <Col
                key={`unavailablevehicle_${index}`}
                className={styles.unavailableVehicleCard}
                lg={4}
                xl={3}
                md={4}
                xxl={2}
              >
                <span className={styles.board}>{vehicle.board}</span>
                <span className={styles.modelcolor}>{`${vehicle.model} ${vehicle.color}`}</span>
                <span className={styles.capacity}>
                  Capacidade:{' '}
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: 15,
                    }}
                  >
                    {vehicle.capacity} kg
                  </span>
                </span>
                <span className={styles.capacity}>
                  Peso alocado:{' '}
                  <span
                    style={{
                      fontWeight: '400',
                      fontSize: 15,
                    }}
                  >
                    {vehicle.allocated_weight ? vehicle.allocated_weight : '0'} kg
                  </span>
                </span>
              </Col>
            ))}
          </Row>
        </Tab>
      </Tabs>

      <Modal centered size="lg" show={showDelivery} onHide={() => setShowDelivery(false)}>
        <Modal.Header closeButton />
        <Modal.Body>
          <span className={styles.infosSpan}>
            <ul>
              <li>
                <b>Endereço:</b> {requestModal?.street}, {requestModal?.street_number},{' '}
                {requestModal?.neighborhood}
                {requestModal?.complement && <span>, {requestModal?.complement}</span>} -{' '}
                {requestModal?.city} - {requestModal?.state}
              </li>
              <li>
                <b>Destinatário:</b> {requestModal?.receiver_name || 'Desconhecido.'}
              </li>
              <li className={styles.modalInvoice}>
                <b>Nota fiscal: </b>
                {requestModal?.invoice}
              </li>
              {requestModal?.request_number && (
                <li>
                  <b>Número do pedido: </b>
                  {requestModal?.request_number}
                </li>
              )}
              {requestModal?.invoice_number && (
                <li>
                  <b>Número da nota fiscal: </b>
                  {requestModal?.invoice_number}
                </li>
              )}

              <li>
                <b>Peso: </b>
                {requestModal?.weight} KG
              </li>
              <li>
                <b>Status: </b>
                {requestModal?.status && (
                  <Badge
                    bg="unset"
                    color="white"
                    pill
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: status[requestModal.status as keyof typeof status].color,
                    }}
                  >
                    {status[requestModal.status as keyof typeof status].name}
                  </Badge>
                )}
              </li>
            </ul>
          </span>
        </Modal.Body>
        <Row className="text-center">
          <hr></hr>
          <Col>
            <Button
              onClick={() => setShowEditRequestModal(true)}
              className={`${styles.modalBtn} ${styles.editBtn}`}
            >
              Editar
            </Button>
            <Button
              onClick={() => setShowDelivery(false)}
              className={`${styles.modalBtn} ${styles.closeBtn}`}
            >
              Fechar
            </Button>
          </Col>
        </Row>
      </Modal>
      <ConfirmationModal
        show={showRedoRequestModal}
        message={redoRequestModalMessage}
        action={() => {
          setShowUpdateRequestWeightModal(true)
        }}
        closeModal={() => setShowRedoRequestModal(false)}
      ></ConfirmationModal>
      <StoreRequestModal
        isOpen={showStoreRequestModal}
        onClose={() => setShowStoreRequestModal(false)}
        onStore={(storedRequest: IRequest) => handleRequestStored(storedRequest)}
      />
      <EditRequestModal
        show={showEditRequestModal}
        onClose={() => setShowEditRequestModal(false)}
        request={requestModal}
        role="delivery-manager"
        onEdit={(updatedRequest: IRequest) => handleRequestUpdate(updatedRequest)}
        onDelete={(removedRequestId: number) => handleRequestRemoved(removedRequestId)}
      />
      {showUpdateRequestWeightModal && redoRequest && (
        <UpdateRequestWeightModal
          show={showUpdateRequestWeightModal}
          requestWeight={redoRequest.weight}
          message={
            'Atualize o peso total do pedido, caso tenha sofrido alguma alteração durante o processo, e confirme.'
          }
          onClose={() => setShowUpdateRequestWeightModal(false)}
          onSubmit={(weight: number) => {
            const updatedRedoRequest = {
              ...redoRequest,
              weight,
            }
            setRedoRequest(updatedRedoRequest)
            handleRedoRequest(updatedRedoRequest)
            setShowRedoRequestModal(false)
            setShowUpdateRequestWeightModal(false)
          }}
        ></UpdateRequestWeightModal>
      )}
    </DashboardLayout>
  )
}

export default Pedidos
